import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { api } from "@/App";
import { toast } from "sonner";
import { 
  Building2, 
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Liste des marques du groupe
const AVAILABLE_BRANDS = [
  "Peugeot",
  "MG",
  "Kia",
  "Seat",
  "Skoda",
  "Cupra",
  "Aixam",
  "Suzuki",
  "Caterham",
  "Omoda Jaecoo",
  "VO",
  "Multimarques"
];

export default function ConcessionsPage() {
  const [concessions, setConcessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedConcession, setSelectedConcession] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    brands: [],
    budget_vn: 0,
    budget_vo: 0
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConcessions();
  }, []);

  const fetchConcessions = async () => {
    try {
      const res = await api.get("/concessions");
      setConcessions(res.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des concessions");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (concession = null) => {
    if (concession) {
      setSelectedConcession(concession);
      // Convertir le champ brand (string) en tableau brands
      let brands = [];
      if (concession.brands && Array.isArray(concession.brands)) {
        brands = concession.brands;
      } else if (concession.brand) {
        // Ancien format: string séparé par virgules
        brands = concession.brand.split(',').map(b => b.trim()).filter(b => b);
      }
      setFormData({
        name: concession.name,
        brands: brands,
        budget_vn: concession.budget_vn || 0,
        budget_vo: concession.budget_vo || 0
      });
    } else {
      setSelectedConcession(null);
      setFormData({ name: "", brands: [], budget_vn: 0, budget_vo: 0 });
    }
    setDialogOpen(true);
  };

  const toggleBrand = (brand) => {
    setFormData(prev => ({
      ...prev,
      brands: prev.brands.includes(brand)
        ? prev.brands.filter(b => b !== brand)
        : [...prev.brands, brand]
    }));
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error("Veuillez saisir le nom de la concession");
      return;
    }

    if (formData.brands.length === 0) {
      toast.error("Veuillez sélectionner au moins une marque");
      return;
    }

    setSaving(true);
    try {
      const dataToSend = {
        name: formData.name,
        brand: formData.brands.join(', '), // Pour compatibilité backend
        brands: formData.brands,
        budget_vn: parseInt(formData.budget_vn) || 0,
        budget_vo: parseInt(formData.budget_vo) || 0
      };

      if (selectedConcession) {
        await api.put(`/concessions/${selectedConcession.id}`, dataToSend);
        toast.success("Concession mise à jour");
      } else {
        await api.post("/concessions", dataToSend);
        toast.success("Concession créée");
      }
      setDialogOpen(false);
      fetchConcessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/concessions/${selectedConcession.id}`);
      toast.success("Concession supprimée");
      setDeleteDialogOpen(false);
      setSelectedConcession(null);
      fetchConcessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const getBrandBadges = (concession) => {
    let brands = [];
    if (concession.brands && Array.isArray(concession.brands)) {
      brands = concession.brands;
    } else if (concession.brand) {
      brands = concession.brand.split(',').map(b => b.trim()).filter(b => b);
    }
    
    return brands.map((brand, idx) => (
      <Badge key={idx} variant="secondary" className="mr-1 mb-1">
        {brand}
      </Badge>
    ));
  };

  if (loading) {
    return (
      <Layout title="Concessions">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#E30613]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Gestion des concessions">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-gray-500">
          {concessions.length} concession{concessions.length > 1 ? 's' : ''} active{concessions.length > 1 ? 's' : ''}
        </p>
        <Button
          onClick={() => handleOpenDialog()}
          className="btn-primary"
          data-testid="add-concession-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle concession
        </Button>
      </div>

      {/* Table */}
      <div className="card-dashboard" data-testid="concessions-table">
        {concessions.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Marques distribuées</TableHead>
                <TableHead className="text-center">Budget VN</TableHead>
                <TableHead className="text-center">Budget VO</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {concessions.map((concession) => (
                <TableRow key={concession.id}>
                  <TableCell className="font-medium">{concession.name}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap">
                      {getBrandBadges(concession)}
                    </div>
                  </TableCell>
                  <TableCell className="text-center font-semibold">
                    {concession.budget_vn || 0}
                  </TableCell>
                  <TableCell className="text-center font-semibold">
                    {concession.budget_vo || 0}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleOpenDialog(concession)}
                        data-testid={`edit-concession-${concession.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setSelectedConcession(concession);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-concession-${concession.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="empty-state">
            <Building2 className="empty-state-icon" />
            <p>Aucune concession</p>
            <Button
              onClick={() => handleOpenDialog()}
              className="mt-4 btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer une concession
            </Button>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              {selectedConcession ? "Modifier la concession" : "Nouvelle concession"}
            </DialogTitle>
            <DialogDescription>
              {selectedConcession 
                ? "Modifiez les informations de la concession"
                : "Ajoutez une nouvelle concession au groupe"
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom de la concession *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: LDA, PEUGEOT LOMME..."
                data-testid="concession-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Marques distribuées *</Label>
              <p className="text-sm text-gray-500 mb-2">Sélectionnez une ou plusieurs marques</p>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_BRANDS.map((brand) => (
                  <button
                    key={brand}
                    type="button"
                    onClick={() => toggleBrand(brand)}
                    className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all flex items-center gap-2 ${
                      formData.brands.includes(brand)
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                    data-testid={`brand-${brand.toLowerCase()}`}
                  >
                    {formData.brands.includes(brand) && (
                      <Check className="w-4 h-4" />
                    )}
                    {brand}
                  </button>
                ))}
              </div>
            </div>

            {/* Budgets annuels */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold text-[#212E38]">Budgets annuels</Label>
              <p className="text-sm text-gray-500 mb-3">Définissez les objectifs annuels de la concession</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="budget_vn">Budget VN (véhicules)</Label>
                  <Input
                    id="budget_vn"
                    type="number"
                    min="0"
                    value={formData.budget_vn}
                    onChange={(e) => setFormData({ ...formData, budget_vn: e.target.value })}
                    placeholder="0"
                    data-testid="budget-vn-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="budget_vo">Budget VO (véhicules)</Label>
                  <Input
                    id="budget_vo"
                    type="number"
                    min="0"
                    value={formData.budget_vo}
                    onChange={(e) => setFormData({ ...formData, budget_vo: e.target.value })}
                    placeholder="0"
                    data-testid="budget-vo-input"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              className="btn-primary"
              disabled={saving}
              data-testid="save-concession-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                selectedConcession ? "Mettre à jour" : "Créer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette concession ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action désactivera la concession "{selectedConcession?.name}". 
              Les vendeurs et objectifs associés seront conservés mais la concession 
              ne sera plus visible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
